import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
import { queryElevateAutocomplete, queryElevateEmptyRecommendations } from '@theme/elevate-autocomplete-api';
import { getElevateClient } from '@theme/elevate-client';
import { resolveUserContext } from '@theme/elevate-products';
import { collectionPlpViewMethods } from '@theme/collection-plp-view';

const PREVIEW_PRODUCT_LIMIT = 8;

/**
 * Collection and search pages already include this stylesheet. Everywhere else
 * it waits until the visitor reaches for search, so it stays off the first paint.
 * @param {string | undefined} href
 */
function ensureCollectionPlpStyles(href) {
  if (!href || document.querySelector('link[href*="collection-plp.css"]')) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.append(link);
}

function appendHighlightedPhrase(target, highlighted, fallback) {
  const parts = typeof highlighted === 'string' ? highlighted.split(/([{}])/) : [];
  let highlightedPart = false;

  if (!parts.length) {
    target.textContent = fallback;
    return;
  }

  for (const part of parts) {
    if (part === '{') {
      highlightedPart = true;
      continue;
    }
    if (part === '}') {
      highlightedPart = false;
      continue;
    }
    if (!part) continue;

    const node = highlightedPart ? document.createElement('mark') : document.createTextNode(part);
    if (node instanceof HTMLElement) node.textContent = part;
    target.append(node);
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #emptyRecommendationProducts;

  /**
   * Get the dialog component.
   * @returns {import('@theme/dialog').DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  get cardTemplate() {
    return this.querySelector('[data-card-template] template');
  }

  get currencyCode() {
    return this.getAttribute('currency-code');
  }

  get locale() {
    return this.getAttribute('locale');
  }

  getLabel(name, fallback) {
    return this.getAttribute(name) || fallback;
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      document.addEventListener('pointerdown', this.#prepareSearchStyles, { signal, capture: true });
      document.addEventListener('focusin', this.#prepareSearchStyles, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });

      if (dialog.refs?.dialog?.open) this.#handleLateDialogOpen();
    }

    this.addEventListener('click', this.#handleElevateClick, { signal });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  #handleElevateClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    if (link?.hasAttribute('data-brand-link')) return;

    const card = link?.closest('[data-card]');
    const ticket = link?.dataset.elevateTicket || card?.dataset.collectionTicket;
    if (!ticket || resolveUserContext('automatic') === 'ANONYMOUS') return;

    getElevateClient()
      .then((client) => client.notify.click(ticket))
      .catch(() => {});
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #prepareSearchStyles = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('search-button')) return;

    ensureCollectionPlpStyles(this.dataset.collectionPlpCss);
  };

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      ensureCollectionPlpStyles(this.dataset.collectionPlpCss);
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    ensureCollectionPlpStyles(this.dataset.collectionPlpCss);
    if (!this.#emptyStateLoaded) this.#loadEmptyState();
    this.#revealSearchDialog();
  };

  /**
   * component-loader.js defines this element after page load, so the dialog can already be
   * open, and typed into, by the time it connects. Keep what was typed instead of resetting.
   */
  #handleLateDialogOpen() {
    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm) {
      this.#handleDialogOpen();
      return;
    }

    ensureCollectionPlpStyles(this.dataset.collectionPlpCss);
    this.#getSearchResults(searchTerm);
  }

  /**
   * Safari can ignore a CSS animation that begins in the same turn as showModal().
   * On desktop the reveal starts fully clipped and fill-mode used to leave it there,
   * so the backdrop showed and the panel did not. Start the animation on the next frame.
   */
  #revealSearchDialog() {
    const dialog = this.dialog?.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;

    dialog.classList.add('search-modal__content--preparing');

    requestAnimationFrame(() => {
      dialog.classList.remove('search-modal__content--preparing');
    });
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list',
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /** Fetch and render Elevate autocomplete suggestions. */
  async #getSearchResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();
    const deferredPromise = SearchUpdateEvent.createPromise();

    predictiveSearchResults.setAttribute('aria-busy', 'true');
    predictiveSearchResults.replaceChildren(this.#createLoadingState(PREVIEW_PRODUCT_LIMIT));

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      }),
    );

    queryElevateAutocomplete(searchTerm)
      .then((result) => {
        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        predictiveSearchResults.removeAttribute('aria-busy');
        predictiveSearchResults.replaceChildren(this.#createElevateResults(result, searchTerm));
        this.#setViewAllLabel(result.totalHits);
        this.#resetScrollPositions();
        const resultCount = result.products.length + result.phraseSuggestions.length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;

        predictiveSearchResults.removeAttribute('aria-busy');
        predictiveSearchResults.replaceChildren(this.#createLoadErrorState());
        console.warn('[Elevate predictive search] Unable to load autocomplete results:', error);
      });
  }

  #createLoadingState(count) {
    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';

    const status = document.createElement('div');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.textContent = this.getAttribute('loading-text') || 'Loading';

    const skeleton = document.createElement('div');
    skeleton.className = 'predictive-search-results__products predictive-search-results__skeleton';
    skeleton.setAttribute('aria-hidden', 'true');

    const title = document.createElement('div');
    title.className = 'predictive-search-results__skeleton-title';

    const list = document.createElement('div');
    list.className = 'predictive-search-results__wrapper-products';

    for (let index = 0; index < count; index += 1) {
      const card = document.createElement('div');
      card.className = 'predictive-search-results__skeleton-card';

      const media = document.createElement('div');
      media.className = 'predictive-search-results__skeleton-media';

      const productTitle = document.createElement('div');
      productTitle.className =
        'predictive-search-results__skeleton-line predictive-search-results__skeleton-line--title';

      const price = document.createElement('div');
      price.className = 'predictive-search-results__skeleton-line predictive-search-results__skeleton-line--price';

      card.append(media, productTitle, price);
      list.append(card);
    }

    skeleton.append(title, list);
    inner.append(status, skeleton);
    return inner;
  }

  #createLoadErrorState() {
    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';

    const message = document.createElement('p');
    message.className = 'predictive-search-results__no-results';
    message.setAttribute('role', 'alert');
    message.textContent = this.getAttribute('load-error-text') || 'Unable to load results.';
    inner.append(message);

    return inner;
  }

  #createElevateResults(result, searchTerm) {
    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';
    inner.dataset.searchResults = '';

    const status = document.createElement('div');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    const resultCount = result.products.length + result.phraseSuggestions.length;
    status.textContent = resultCount
      ? `${resultCount} suggestions available for ${searchTerm}`
      : this.getAttribute('no-results-text-template')?.replace('__query__', searchTerm) ||
        `No results found for “${searchTerm}”.`;
    inner.append(status);

    if (result.phraseSuggestions.length) {
      const suggestionsTitle = document.createElement('h4');
      suggestionsTitle.className = 'predictive-search-results__title predictive-search-results__title--queries';
      suggestionsTitle.textContent = this.getAttribute('suggestions-title') || 'Popular searches';

      const suggestions = document.createElement('ul');
      suggestions.className =
        'predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-queries list-unstyled';

      for (const suggestion of result.phraseSuggestions) {
        const item = document.createElement('li');
        item.className = 'predictive-search-results__card--query';
        item.setAttribute('ref', 'resultsItems[]');

        const link = document.createElement('a');
        link.className = 'pills__pill predictive-search-results__pill';
        const url = new URL(Theme.routes.search_url, location.origin);
        url.searchParams.set('q', suggestion.q);
        url.searchParams.set('elevate-origin', 'PHRASE_SUGGEST');
        link.href = url;
        link.setAttribute('aria-label', suggestion.q);

        const text = document.createElement('span');
        appendHighlightedPhrase(text, suggestion.highlighted, suggestion.q);
        link.append(text);
        item.append(link);
        suggestions.append(item);
      }
      inner.append(suggestionsTitle, suggestions);
    }

    if (result.products.length) inner.append(this.#createElevateProducts(result.products));

    if (!resultCount) {
      const noResults = document.createElement('p');
      noResults.className = 'predictive-search-results__no-results';
      noResults.textContent =
        this.getAttribute('no-results-text-template')?.replace('__query__', searchTerm) ||
        `No results found for “${searchTerm}”.`;
      inner.append(noResults);
    }

    if (result.redirectLink) {
      const redirect = document.createElement('div');
      redirect.dataset.singleResultUrl = result.redirectLink;
      inner.append(redirect);
    }

    return inner;
  }

  #createElevateProducts(products, heading) {
    const wrapper = document.createElement('div');
    wrapper.id = 'predictive-search-products';
    wrapper.className = 'predictive-search-results__products';

    const title = document.createElement('h4');
    title.className = 'predictive-search-results__title';
    title.textContent = heading || this.getAttribute('products-title') || 'Products';

    const list = document.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';

    for (const product of products.slice(0, PREVIEW_PRODUCT_LIMIT)) {
      const item = collectionPlpViewMethods.createProductCard.call(this, product);
      item.classList.add('predictive-search-results__card', 'predictive-search-results__card--product');
      item.setAttribute('ref', 'resultsItems[]');
      list.append(item);
    }

    wrapper.append(title, list);
    return wrapper;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const showRecentlyViewed = this.getAttribute('show-recently-viewed') === 'true';
    const viewedProducts = showRecentlyViewed ? RecentlyViewed.getProducts() : [];
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #setViewAllLabel(totalHits = 0) {
    const button = this.refs.viewAllButton;
    if (!(button instanceof HTMLElement)) return;

    const label = this.getAttribute('view-all-text') || 'View all';
    button.textContent = totalHits > 0 ? `${label} (${totalHits})` : label;
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#setViewAllLabel(0);

    const abortController = this.#createAbortController();
    predictiveSearchResults.setAttribute('aria-busy', 'true');
    predictiveSearchResults.replaceChildren(
      this.#createLoadingState(Number(this.getAttribute('empty-product-limit')) || 4),
    );
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    let emptySectionMarkup;
    try {
      emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    } catch (error) {
      if (abortController.signal.aborted) return;
      predictiveSearchResults.removeAttribute('aria-busy');
      predictiveSearchResults.replaceChildren(this.#createLoadErrorState());
      console.warn('[Predictive search] Unable to load the empty state:', error);
      return;
    }

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) {
      predictiveSearchResults.removeAttribute('aria-busy');
      predictiveSearchResults.replaceChildren(this.#createLoadErrorState());
      console.warn('[Predictive search] No empty section markup found.');
      return;
    }

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const showRecentlyViewed = this.getAttribute('show-recently-viewed') === 'true';
    const viewedProducts = showRecentlyViewed ? RecentlyViewed.getProducts() : [];

    if (viewedProducts.length > 0) {
      try {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          const emptyStateInner = parsedEmptySectionMarkup.querySelector('.predictive-search-results__inner');

          if (recentlyViewedProductsHtml && emptyStateInner) {
            for (const child of recentlyViewedProductsHtml.children) {
              if (child instanceof HTMLElement) {
                child.setAttribute('ref', 'recentlyViewedWrapper');
              }
            }

            emptyStateInner.prepend(...recentlyViewedProductsHtml.children);
          }
        }
      } catch (error) {
        console.warn('[Predictive search] Unable to load recently viewed products:', error);
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    await this.#renderElevateEmptyState(predictiveSearchResults, abortController);
    predictiveSearchResults.removeAttribute('aria-busy');
    this.#resetScrollPositions();
  };

  async #renderElevateEmptyState(predictiveSearchResults, abortController) {
    if (this.getAttribute('empty-source') !== 'elevate') return;

    const inner = predictiveSearchResults.querySelector('.predictive-search-results__inner');
    if (!(inner instanceof HTMLElement)) return;

    try {
      if (!this.#emptyRecommendationProducts) {
        const result = await queryElevateEmptyRecommendations({
          algorithm: this.getAttribute('empty-algorithm') || 'TOP_PRODUCTS',
          limit: this.getAttribute('empty-product-limit') || 4,
          productRules: this.getAttribute('empty-product-rules') || '',
          userContext: this.getAttribute('empty-user-context') || 'automatic',
        });
        this.#emptyRecommendationProducts = result.products;
      }

      if (abortController.signal.aborted) return;
      inner.querySelector('.predictive-search-results__empty-loading')?.remove();
      inner.querySelector('.predictive-search-results__skeleton')?.remove();

      if (this.#emptyRecommendationProducts.length) {
        inner.append(
          this.#createElevateProducts(
            this.#emptyRecommendationProducts,
            this.getAttribute('empty-products-title') || 'Recommended for you',
          ),
        );
      }
    } catch (error) {
      if (abortController.signal.aborted) return;
      inner.querySelector('.predictive-search-results__skeleton')?.remove();
      const status = inner.querySelector('.predictive-search-results__empty-loading');
      if (status) {
        status.classList.remove('visually-hidden');
        status.classList.add('predictive-search-results__no-results');
        status.textContent = this.getAttribute('no-recommendations-text') || 'No recommendations available.';
      }
      console.warn('[Elevate predictive search] Unable to load empty-state recommendations:', error);
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
